import { ApiRoutes } from "consts/enpoints/api";
import { WebRoutes } from "consts/enpoints/web";
import { ECommerceOnboardingFilableResource } from "consts/ecommerce";
import { when } from "util/support";
import type { FilableResourceResponse } from "types";
import { useFileUpload } from "hooks";
import { FormAdapter } from "hooks/useForm";
import BusinessLogicException from "exceptions/BusinessLogicException";
import type { Merchant as MerchantType } from "features/ecommerce/types";
import { Merchant as MerchantApi } from "features/ecommerce/api";
import { Merchant } from "ui/forms/ECommerce";
import { useOnboardingForm, useOnboardingStepData } from "ui/organizms/ECommerce/Onboarding/common";
import { useOnboardingApplication } from "../../hooks";

export const useBankingAndFinance = () => {
    const { getApplicationModel } = useOnboardingApplication();

    const { data } = useOnboardingStepData<MerchantType.OnboardingBankingAndFinance>();

    const uploadFile = useFileUpload();

    const withValues = useOnboardingForm({
        save: MerchantApi.putOnboardingsBillingFinanceSteps,
        route: WebRoutes.Onboarding.Merchant,
        getApplicationModel
    });

    const formProps: Merchant.BankingAndFinance.OnboardingBankingAndFinanceForm = {
        bootstrapData: {},
        initialValues: Merchant.BankingAndFinance.getInitialValues(data),
        isBootstrapDataLoading: false,
        onSaveOrCreate: async ({
            accountHolderName,
            bankName,
            iban,
            swift,
            currency,
            avgTransactionSize,
            monthlyIncome,
            transactionVolume,
            proofFileKey
        }, helpers) => {
            if (!(proofFileKey instanceof File)) {
                throw new BusinessLogicException(
                    'Proof file must be a File instance',
                    { proofFileKey }
                );
            }

            const fileResponse: FilableResourceResponse = await uploadFile({
                url: ApiRoutes.PostOnboardingsFiles(),
                files: [
                    {
                        file: proofFileKey,
                        field: Merchant.BankingAndFinance.FormField.ProofFileKey,
                        resource: ECommerceOnboardingFilableResource.BillingFinanceSteps
                    }
                ]
            });

            return when(
                fileResponse.success,
                () => withValues({
                    accountHolderName,
                    bankName,
                    iban,
                    swift: swift || null,
                    currency: String(currency),
                    avgTransactionSize,
                    monthlyIncome,
                    transactionVolume
                }, helpers),
                () => FormAdapter.fromResponse(fileResponse)
            )
        }
    };

    return {
        ...formProps,
        ...Merchant.BankingAndFinance.useForm(formProps),
        ...data
    };
};

export default useBankingAndFinance;
